import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import { OverlayProvider } from 'react-aria';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { Link } from '../Link/Link';
import { IconNavigationArrowRight, IconNavigationCheckmarkOutline } from '../Icons';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { MDXProvider } from '@mdx-js/react';
import animateScrollTo from 'animated-scroll-to';
import throttle from 'lodash.throttle';

import webEditorImage from '../product/images/SoftwareWebEditor.svg';
import ideImage from '../product/images/SoftwareIDE.svg';
import plcImage from '../product/images/SoftwarePLC.svg';

import ProductSuggestions from '../product/ProductSuggestions';

import useWindowSize from '../../hooks/useWindowSize';
import useTouchOutside from '../../hooks/useTouchOutside';

import './GettingStartedModal.scss';
import { AccordionList } from '../AccordionList/AccordionList';

interface TocSubLevelprops {
  tocEntries: Array<any>;
  setMenuMobileExpanded: (value: boolean) => any;
  isMobile: boolean;
}

const TocSublevel = ({ tocEntries, setMenuMobileExpanded, isMobile }: TocSubLevelprops) => {
  const mainElement = document.getElementById('gs-modal-main');
  const [active, setActive] = useState(null);

  // Add scroll event listener to update currently active heading.
  useEffect(() => {
    const nodes = tocEntries.map((el) => document.getElementById(el.url.replace('#', '')));
    // Throttling the scrollHandler saves computation and hence battery life.
    const scrollHandler = throttle(() => {
      const offsets = nodes.map((el) => el.offsetTop);
      const activeIndex = offsets.findIndex((offset) => offset > mainElement.scrollTop + (isMobile ? 60 : 10));
      setActive(activeIndex === -1 ? nodes.length - 1 : activeIndex - 1);
    }, 300);
    mainElement.addEventListener(`scroll`, scrollHandler);
    return () => mainElement.removeEventListener(`scroll`, scrollHandler);
  }, [isMobile, mainElement, tocEntries]);

  return tocEntries.map((e, i) => (
    <a
      key={i}
      className={classnames('tocItem', { active: active === i })}
      href={e.url}
      onClick={(event) => {
        event.preventDefault();
        animateScrollTo(document.getElementById(e.url.slice(1)), {
          verticalOffset: isMobile ? -60 : -10,
          speed: 200,
          elementToScroll: mainElement,
        });
        setMenuMobileExpanded(false);
      }}
    >
      {e.title}
    </a>
  ));
};

interface WizardStepsProps {
  steps: any;
  currentStep: number;
  setStep: (value: number) => any;
  setMenuMobileExpanded: (value: any) => any;
  isMobile: boolean;
}

const WizardSteps = ({ steps, currentStep, setStep, setMenuMobileExpanded, isMobile }: WizardStepsProps) => {
  const mainElement = document.getElementById('gs-modal-main');

  const accordions = steps?.map((el, i) => ({
    defaultExpanded: currentStep - 1 === i,
    iconPosition: 'right',
    id: el.node.childMdx.tableOfContents.items[0].title,
    label: el.node.childMdx.tableOfContents.items[0].title,
    panel: currentStep - 1 === i && (
      <TocSublevel tocEntries={el.node.childMdx.tableOfContents.items[0].items} setMenuMobileExpanded={setMenuMobileExpanded} isMobile={isMobile} />
    ),
    onExpand: () => {
      setStep(i + 1);
      if (!isMobile) {
        mainElement.scrollTop = 0;
      }
    },
  }));
  if (!accordions) return null;
  return <AccordionList className="modal-gs-accordion" accordions={accordions} />;
};

interface NewTabLinkProps {
  children: React.ReactNode;
  href: string;
}

const NewTabLink = (props: NewTabLinkProps) => (
  <a href={props.href} target="_blank" rel="noopener noreferrer">
    {props.children}
  </a>
);

interface WizardContentProps {
  data: any;
  currentStep: number;
  setStep: (value: number) => any;
  setMenuMobileExpanded: (value: boolean) => any;
}

const WizardContent = ({ data, currentStep, setStep, setMenuMobileExpanded }: WizardContentProps) => {
  return (
    <div>
      <MDXProvider
        components={{
          a: NewTabLink,
        }}
      >
        <MDXRenderer>{data.node.childMdx.body}</MDXRenderer>
      </MDXProvider>
      <div className={'modalSpacer'}></div>
      <div className={'blur'}></div>
      <div className={'wrapperFloatingNext'}>
        <Button
          onClick={() => {
            setStep(currentStep + 1);
            document.getElementById('gs-modal-main').scrollTop = 0;
            setMenuMobileExpanded(false);
          }}
        >
          {'Next'}
        </Button>
      </div>
    </div>
  );
};

interface SoftwareCardProps {
  icon: string;
  title: string;
  description: string;
  handleSelect: () => any;
  selected: boolean;
}

const SoftwareCard = ({ icon, title, description, handleSelect, selected }: SoftwareCardProps) => (
  <div className={classnames('card', { selected: selected })} onClick={() => handleSelect()}>
    <span className={'title'}>
      <img src={icon} alt={title} />
      {title}
    </span>
    <p className={'description'}>{description}</p>
  </div>
);

interface GettingStartedModalProps {
  data: any;
  isOpen: boolean;
  onClose: () => any;
  core: string;
  suggestions: any;
  getInspired?: any;
  product: any;
  chosenSoftware: string;
}

const GettingStartedModal = (props: GettingStartedModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [chosenSoftware, setChosenSoftware] = useState(null);
  const [menuMobileExpanded, setMenuMobileExpanded] = useState(false);
  const size = useWindowSize();
  const menuRef = useRef(null);
  // Close the menu if click outside it
  useTouchOutside(menuRef, () => setMenuMobileExpanded(false));

  // Some operating systems don't support colons. Therefore we replace those with a dash.
  const sanitizedCoreName = props.core?.replace(/:/g, '-');

  const reset = () => {
    setChosenSoftware(null);
    setCurrentStep(0);
    setSelectedSoftware(null);
  };

  useEffect(() => {
    if (props.chosenSoftware) {
      setMenuMobileExpanded(false);
      setCurrentStep(1);
      setSelectedSoftware(props.chosenSoftware);
      // Ugly porkaround to wait the render of the WizardContent
      setTimeout(() => {
        setChosenSoftware(props.chosenSoftware);
      }, 0);
    }
  }, [props.chosenSoftware]);

  // If it's the ide v1 flow, add the install core step to the wizard
  const stepsData = chosenSoftware
    ? chosenSoftware === 'ide-v1' && sanitizedCoreName && props.data['ide-v1'].cores[sanitizedCoreName]
      ? [props.data['ide-v1'].cores[sanitizedCoreName], ...props.data[chosenSoftware].steps]
      : props.data[chosenSoftware]?.steps
    : null;

  const aside = (
    <aside className={'aside'}>
      <div className={classnames('item', { active: currentStep === 0 })} onClick={() => reset()}>
        {'How do you want to program?'}
      </div>
      {chosenSoftware && (
        <WizardSteps
          key={currentStep}
          steps={stepsData?.slice(0, currentStep)}
          currentStep={currentStep}
          setStep={setCurrentStep}
          setMenuMobileExpanded={setMenuMobileExpanded}
          isMobile={size.width <= 768}
        />
      )}
      {chosenSoftware && currentStep > stepsData?.length && <div className="item active">{'Next steps'}</div>}
    </aside>
  );

  const helperFooter = null;

  return (
    <OverlayProvider>
      <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        isDismissable={true}
        title={'Setup Guide'}
        className="gettingstarted-modal"
        back={!!currentStep}
        onBack={() => reset()}
      >
        {/* If mobile viewport wrap the aside element in a collapsible menu and hide it for the "choose software" step */}
        {size.width > 980 ? (
          aside
        ) : currentStep ? (
          <>
            <div className={classnames('menuContainer', { active: menuMobileExpanded })} ref={menuRef}>
              <div className="currentVoiceMenu" onClick={() => setMenuMobileExpanded(!menuMobileExpanded)}>
                {stepsData && stepsData[currentStep - 1] ? stepsData[currentStep - 1].node.childMdx.tableOfContents.items[0].title : 'Next steps'}
              </div>
              <div className="menuContent">{aside}</div>
            </div>
            {size.width <= 480 && <div className={classnames('backdrop', { open: menuMobileExpanded })}></div>}
          </>
        ) : null}
        <main id="gs-modal-main" className={classnames('main', { chooseSoftware: !chosenSoftware })}>
          {chosenSoftware ? (
            currentStep <= stepsData?.length ? (
              <WizardContent
                data={stepsData[currentStep - 1]}
                currentStep={currentStep}
                setStep={setCurrentStep}
                setMenuMobileExpanded={setMenuMobileExpanded}
              ></WizardContent>
            ) : (
              <div className={'nextStepsWrapper'}>
                <h3 className={'disclaimer'}>
                  <IconNavigationCheckmarkOutline />
                  {'All done! Here are the next steps...'}
                </h3>
                <h2>{`Get More Out of the ${props.product.name}`}</h2>
                <p className={'description'}>
                  {`Below you can find some hand picked tutorials for the ${props.product.name} `}
                  {'and useful links to learn the basics of Arduino. You can also visit the full documentation. '}
                </p>
                <Link rel="noreferrer" variant="secondary" href={props.product.slug} className="product-page-button">
                  LEARN MORE <IconNavigationArrowRight />
                </Link>
                {props.suggestions && (
                  <div className={'suggestionsWrapper'}>
                    <ProductSuggestions suggestions={props.suggestions.childMdx} />
                  </div>
                )}
                <div className={'modalSpacer'}></div>
                <div className={'blur'}></div>
              </div>
            )
          ) : (
            <div className={'chooseSoftwareWrapper'}>
              <h1 className={'modalTitle'}>{'How do you want to program?'}</h1>
              <p className={'modalDescription'}>
                {
                  'Arduino has developed software tools that work for beginners as well as advanced users. Select your preferred choice and continue with the quickstart.'
                }
              </p>
              <div className={'softwareWrapper'}>
                {props.product.compatibility?.software?.includes('arduino-ide') && (
                  <>
                    <SoftwareCard
                      icon={ideImage}
                      title={'Arduino IDE 2.0'}
                      description={
                        'With features like debugging and code highlighting the new Arduino IDE 2 makes coding faster and easier. A great choice for both beginners and more advanced makers.'
                      }
                      handleSelect={() => setSelectedSoftware('ide-v2')}
                      selected={selectedSoftware === 'ide-v2'}
                    />
                    <SoftwareCard
                      icon={ideImage}
                      title={'Arduino IDE 1.8'}
                      description={
                        'The classic offline editor that has been downloaded over 50 million times. You may choose this if you’re following a book or other projects based on this older version.'
                      }
                      handleSelect={() => setSelectedSoftware('ide-v1')}
                      selected={selectedSoftware === 'ide-v1'}
                    />
                  </>
                )}
                {props.product.compatibility?.software?.includes('web-editor') && (
                  <SoftwareCard
                    icon={webEditorImage}
                    title={'Web Editor'}
                    description={
                      'Our web-based editor with cores and libraries already installed. Ideal for beginners and who needs to keep their sketches saved securely in the cloud and synced across devices.'
                    }
                    handleSelect={() => setSelectedSoftware('web-editor')}
                    selected={selectedSoftware === 'web-editor'}
                  />
                )}
                {props.product.compatibility?.software?.includes('plc-ide') && (
                  <SoftwareCard
                    icon={plcImage}
                    title={'Arduino PLC IDE'}
                    description={
                      'The IDE that allows you to program using IEC 61131-3 standard languages. You can use the 5 programming languages defined by that standard and Arduino sketches at the same time.'
                    }
                    handleSelect={() => setSelectedSoftware('plc-ide')}
                    selected={selectedSoftware === 'plc-ide'}
                  />
                )}
              </div>
              <div className={'buttonWrapper'}>
                <Button
                  isDisabled={!selectedSoftware}
                  onClick={() => {
                    setMenuMobileExpanded(false);
                    setCurrentStep(1);
                    setChosenSoftware(selectedSoftware);
                  }}
                >
                  {'Next'}
                </Button>
                {helperFooter}
              </div>
              <div className={'spacer'}></div>
              {helperFooter}
            </div>
          )}
        </main>
      </Modal>
    </OverlayProvider>
  );
};

export default GettingStartedModal;
